import { formatNumber } from "./formatNumber";
import { getPercentageDifference } from "./getPercentageDifference";
import { getPreviousMonth } from "./getPreviousMonth";

/**
 * @param {string} type Type of value
 * @param {string} language Language
 * @returns String with the type of value
 */
function getValueType(type, language) {
  let valueType;
  switch (type) {
    case "tweets":
      valueType = "Tweets";
      break;
    case "noticias":
      valueType = language === "en" ? "News" : "Noticias";
      break;
    case "reachPos":
      valueType = "Tweets";
      break;
    case "accounts":
      valueType = language === "en" ? "Accounts" : "Cuentas";
      break;
    default:
      valueType = language === "en" ? "Value" : "Valor";
      break;
  }

  return valueType;
}

/**
 *
 * @param {Object} params Object with params from echarts tooltip formatter
 * @param {string} type Type of values
 * @param {string} selectedYear Selected year
 * @param {number} previousValue Previous year to selected one
 * @param {boolean} isPercentage Boolean determining if the value is a percentage or not
 * @param {string} language Language
 *
 * @returns HTML as string with tooltip contents
 */
function makeLineToolTip(
  params,
  type,
  selectedYear,
  previousValue,
  isPercentage,
  language
) {
  if (params === undefined || params === null) return <></>;
  const percDifference = isPercentage
    ? (params.data - previousValue).toFixed(1)
    : getPercentageDifference(params.data, previousValue);
  return `
    <div>
      <span>${params.axisValueLabel}${
    selectedYear !== "HIST" ? ", " + selectedYear : ""
  }</span>
      <div >
        ${getValueType(type, language)}: <span>${formatNumber(
    isPercentage ? params.data.toFixed(1) : params.data,
    language
  )}${isPercentage ? "%" : ""}</span>
      </div>
      <div>
        ${
          language === "en"
            ? "Change compared to previous month:"
            : "Cambio respecto al mes anterior:"
        }
        <span style='color: ${percDifference < 0 ? "#FC4D4D" : "#30A98C"}'>${
    previousValue
      ? (percDifference < 0 ? "" : "+") +
        formatNumber(percDifference, language) +
        (isPercentage ? "pp" : "%")
      : language === "en"
      ? "No previous data"
      : "Sin datos previos"
  }<span>
      </div>
    </div>`;
}

/**
 *
 * @param {Object} params Object with params from echarts tooltip formatter
 * @param {string} type Type of values
 * @param {string} selectedYear Selected year
 * @param {Object} data Object with graph data
 * @param {Array <string>} yearOptions Array with year options
 * @param {boolean} isPercentage Boolean determining if the value is a percentage or not
 * @param {string} language Language
 * @returns HTML as string with tooltip contents
 */
export function generateLineToolTip(
  params,
  type,
  selectedYear,
  data,
  yearOptions,
  isPercentage,
  language
) {
  let previousValue;

  if (selectedYear === "HIST") {
    const index = data.dates.indexOf(params[0].axisValueLabel);
    if (index === 0) previousValue = null;
    else previousValue = data.data[index - 1];
  } else {
    const previousMonth = getPreviousMonth(
      selectedYear,
      params[0].dataIndex + 1,
      yearOptions
    );
    if (previousMonth === null) previousValue = null;
    else {
      if (previousMonth.year !== selectedYear) {
        previousValue = null;
      } else {
        previousValue = data.data[parseInt(previousMonth.month) - 1];
      }
    }
  }

  return makeLineToolTip(
    params[0],
    type,
    selectedYear,
    previousValue,
    isPercentage,
    language
  );
}

/**
 *
 * @param {Object} params Object with params from echarts tooltip formatter
 * @param {Object} data Object with graph data
 * @param {boolean} isPercentage Boolean determining if the value is a percentage or not
 * @param {string} language Language
 * @param {boolean} areColorsSwitched Boolean that determines if colors are switched
 *
 * @returns HTML as string with tooltip contents
 */
export function generateMultiplLineTooltip(
  params,
  data,
  isPercentage,
  areColorsSwitched,
  language
) {
  if (params === undefined || params === null) return <></>;
  const components = [];
  components.push(`<div><span>${params[0].axisValueLabel}</div></span>`);
  
  params.forEach((line) => {
    if (line.dataIndex === 0) {
      components.push(
        `<div style='display: flex; flex-direction: row; justify-content: space-between;'}}>
            <div style='margin-right: 15px'>${line.marker} ${
          line.seriesName
        }</div>
            <div><b>${formatNumber(
              isPercentage ? line.data.toFixed(1) : line.data,
              language
            )}${isPercentage ? "%" : ""}</b></div>
        </div>`
      );
    } else {
      const currentIndex = line.dataIndex;
      const currentValue = line.data;
      const previousValue = data[line.seriesName][currentIndex - 1];

      const percDifference = isPercentage
        ? ((currentValue?.toFixed(1) || 0) - (previousValue?.toFixed(1) || 0)).toFixed(1)
        : getPercentageDifference(currentValue, previousValue);

      const color = areColorsSwitched
        ? percDifference < 0
          ? "#30A98C"
          : "#FC4D4D"
        : percDifference < 0
        ? "#FC4D4D"
        : "#30A98C";

      components.push(
        `<div style='display: flex; flex-direction: row; justify-content: space-between;'}}>
            <div style='margin-right: 15px'>${line.marker} ${
          line.seriesName
        }</div>
            <div><b>${formatNumber(
              isPercentage ? (line.data?.toFixed(1) || line.data) : line.data,
              language
            )}${isPercentage ? "%" : ""} <span style='color: ${color}'>${
          (percDifference < 0 ? "" : "+") +
          formatNumber(percDifference, language)
        }${isPercentage ? "pp" : "%"}</span></b></div>
        </div>`
      );
    }
  });

  return components.join("");
}
